/**
 * @file FeedRow.tsx
 * @description Строка ленты событий (phase19.31, uiux-design.md §4.1).
 *
 * Раскладка: время · значок в кружке 24 с тоном вида · текст · автор справа.
 * Строка 40px в одну линию, 56 — со второй (каналы уведомления,
 * комментарий). На телефоне автор уходит во вторую линию: справа ему места
 * нет, а обрезать текст события ради имени нельзя.
 */

import type { FeedEntry, FeedTone } from "./feed-types";
import { cn } from "@/lib/utils";

/** Тон значка → подложка и цвет (статусные токены темы, SC26–SC27). */
const TONE_CLASS: Record<FeedTone, string> = {
  progress: "bg-[var(--status-progress-bg)] text-[var(--status-progress-fg)]",
  success: "bg-[var(--status-success-bg)] text-[var(--status-success-fg)]",
  muted: "bg-[var(--status-neutral-bg)] text-[var(--status-neutral-fg)]",
  danger: "bg-[var(--status-error-bg)] text-[var(--status-error-fg)]",
  neutral: "bg-surface-low text-on-surface-variant",
};

interface Props {
  entry: FeedEntry;
  /** Время «22:00» — уже по поясу магазина. */
  time: string;
}

export function FeedRow({ entry, time }: Props) {
  const Icon = entry.icon;
  return (
    <li data-tone={entry.tone} className="flex min-h-10 items-start gap-3 py-2">
      <time dateTime={entry.at} className="type-caption w-11 shrink-0 pt-0.5 tabular-nums text-on-surface-variant">
        {time}
      </time>
      <span aria-hidden className={cn("flex size-6 shrink-0 items-center justify-center rounded-full", TONE_CLASS[entry.tone])}>
        <Icon className="size-3.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="type-body break-words text-on-surface">{entry.text}</p>
        {entry.detail ? (
          <p className="type-caption break-words text-on-surface-variant">
            {entry.detail}
            {/* Телефон: автор — во второй линии. */}
            <span className="sm:hidden"> · {entry.actor}</span>
          </p>
        ) : (
          <p className="type-caption text-on-surface-variant sm:hidden">{entry.actor}</p>
        )}
      </div>
      <span className="type-caption hidden max-w-[12rem] shrink-0 truncate pt-0.5 text-right text-on-surface-variant sm:block">
        {entry.actor}
      </span>
    </li>
  );
}
